import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { Tooltip } from "@material-ui/core";
import { ModalComponent } from "components/ui/modal/Modal";
import ReportList from "components/data-display/report-list/ReportList";
import "components/screenshot/screenshot.css";

const HINT_DURATION = 5000;

/**
 * Captura screenshot do canvas WebGL (Three.js) e inicia o fluxo
 * de ocorrências/relatórios reutilizando `ReportList`.
 */
export function ScreenshotBim({ rendererRef, folderPath, variant }) {
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [image, setImage] = useState({ img: null, base64: "" });

  // Capture hint
  const reports = useSelector(state => state.report?.report || []);
  const prevCountRef = useRef(reports.length);
  const [showHint, setShowHint] = useState(false);
  const [hintLeaving, setHintLeaving] = useState(false);
  const [hintStyle, setHintStyle] = useState({});
  const [hintArrow, setHintArrow] = useState("bottom");
  const toolbarRef = useRef(null);
  const dismissTimerRef = useRef(null);

  const dismissHint = () => {
    setHintLeaving(true);
    setTimeout(() => {
      setShowHint(false);
      setHintLeaving(false);
    }, 260);
  };

  // Compute hint position from the toolbar's bounding rect
  const computeHintPos = () => {
    if (!toolbarRef.current) return;
    const rect = toolbarRef.current.getBoundingClientRect();
    const isInlineVertical = variant === "inline";

    if (isInlineVertical) {
      // Toolbar is embedded on the right side, vertical — hint appears to the LEFT
      setHintArrow("right");
      setHintStyle({
        position: "fixed",
        top: Math.max(8, rect.top + rect.height / 2 - 40),
        right: window.innerWidth - rect.left + 12,
        left: "auto",
        bottom: "auto"
      });
    } else {
      // Bottom bar (default, bim, etc.) — hint appears ABOVE
      setHintArrow("bottom");
      setHintStyle({
        position: "fixed",
        bottom: window.innerHeight - rect.top + 10,
        left: rect.left,
        top: "auto",
        right: "auto"
      });
    }
  };

  useEffect(() => {
    if (reports.length > prevCountRef.current) {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      computeHintPos();
      setShowHint(true);
      setHintLeaving(false);
      dismissTimerRef.current = setTimeout(dismissHint, HINT_DURATION);
    }
    prevCountRef.current = reports.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports.length]);

  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  function handleOpen() {
    setIsOpenModal(true);
  }
  function handleClose() {
    setIsOpenModal(false);
  }

  const openModal = async e => {
    e.preventDefault();
    setIsLoadingModal(true);
    try {
      const domElement = rendererRef?.current?.domElement;
      if (!domElement || typeof domElement.toDataURL !== "function") return;

      // Garante que a renderização está atual antes de capturar.
      await new Promise(resolve => requestAnimationFrame(resolve));

      const image64 = domElement.toDataURL("image/png");
      const imageFile = await new Promise(resolve => {
        domElement.toBlob(
          blob => {
            if (!blob) return resolve(null);
            resolve(new File([blob], "screenshot.png", { type: "image/png" }));
          },
          "image/png",
          1
        );
      });

      if (!imageFile) return;

      setImage({ img: imageFile, base64: image64 });
      handleOpen();
    } catch (err) {
      // Intencionalmente silencioso: o usuário só precisa saber que falhou.
      // (A UI do modal não abre se não houver screenshot.)
      // eslint-disable-next-line no-console
      console.error("[ScreenshotBim] Erro ao capturar screenshot:", err);
    } finally {
      setIsLoadingModal(false);
    }
  };

  const toolbarClass = [
    "screenshot-toolbar",
    variant ? `screenshot-toolbar--${variant}` : "",
    showHint ? "screenshot-toolbar--hint" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div ref={toolbarRef} className={toolbarClass}>
        {isLoadingModal ? (
          <div className="screenshot-toolbar__spinner" role="status">
            <div className="spinner-border text-primary" />
          </div>
        ) : (
          <Tooltip title="Capturar e criar ocorrência (anote com lápis, círculo, quadrado e cores)">
            <button
              type="button"
              onClick={openModal}
              className="screenshot-toolbar__btn"
              aria-label="Capturar snapshot e anotar"
            >
              <i className="fas fa-camera" />
            </button>
          </Tooltip>
        )}
        <ReportList folderPath={folderPath} />
      </div>

      {showHint &&
        createPortal(
          <div
            className={`capture-hint capture-hint--arrow-${hintArrow}${hintLeaving ? " capture-hint--leaving" : ""}`}
            style={{ ...hintStyle, "--hint-duration": `${HINT_DURATION}ms` }}
            onClick={dismissHint}
            role="status"
            aria-live="polite"
          >
            <div className="capture-hint__body">
              <div className="capture-hint__check">
                <i className="fas fa-check" />
              </div>
              <div className="capture-hint__text">
                <span className="capture-hint__title">Captura registrada</span>
                <span className="capture-hint__sub">
                  Clique em <i className="fas fa-file-alt" /> para revisar e salvar
                </span>
              </div>
              <div className="capture-hint__arrow">
                <i className="fas fa-chevron-right" />
              </div>
            </div>
            <div className="capture-hint__timer">
              <div className="capture-hint__timer-fill" />
            </div>
          </div>,
          document.body
        )}

      {createPortal(
        <ModalComponent
          show={isOpenModal}
          closeModal={handleClose}
          image={image}
          variant={variant === "bim" ? "inline" : variant}
        />,
        document.body
      )}
    </>
  );
}

export default ScreenshotBim;

